/**
 * Demo read-only mode helpers (frontend).
 *
 * The backend merges a `demo` block into the entitlements payload:
 *   entitlements.demo = {
 *     isDemoUser: boolean,
 *     readOnly:   boolean,
 *     policies:   {
 *       [featureKey]: 'allow' | 'read_only' | 'hidden' | 'visible' | 'disabled'
 *     }
 *   }
 *
 * NAV keys use visible / disabled / hidden.
 * ACTION keys use allow / read_only / hidden.
 */

/** True when the current account is a read-only demo account. */
export function isDemoUser(entitlements) {
  return Boolean(entitlements?.demo?.isDemoUser);
}

/** feature_key -> access map for the current demo account (empty when not demo). */
export function demoPolicies(entitlements) {
  return entitlements?.demo?.policies || {};
}

/**
 * Resolved access for a feature key.
 * Non-demo users always get 'allow'. Demo users get the stored access or `null`
 * (meaning "use the caller's default").
 */
export function featureAccess(entitlements, featureKey) {
  if (!isDemoUser(entitlements)) return 'allow';
  return demoPolicies(entitlements)[featureKey] || null;
}

/** True when a nav item / section / feature is explicitly hidden for this demo account. */
export function isFeatureHidden(entitlements, featureKey) {
  if (!isDemoUser(entitlements)) return false;
  return demoPolicies(entitlements)[featureKey] === 'hidden';
}

/** True when a nav item / section is visible but locked (demo disabled state). */
export function isFeatureDisabled(entitlements, featureKey) {
  if (!isDemoUser(entitlements)) return false;
  return demoPolicies(entitlements)[featureKey] === 'disabled';
}

/**
 * Whether a write action is permitted for the current account.
 * Demo accounts are read-only by default; only an explicit 'allow' row re-enables a write.
 */
export function canWrite(entitlements, featureKey) {
  if (!isDemoUser(entitlements)) return true;
  const access = featureAccess(entitlements, featureKey);
  return access === 'allow' || access === 'visible';
}

/** True when the whole account should be treated as read-only. */
export function isReadOnly(entitlements) {
  return Boolean(entitlements?.demo?.readOnly);
}

/** Standard copy for locked demo nav / route guard dialogs. */
export const DEMO_LOCKED_MESSAGE =
  'This action isn\'t available in demo mode. Contact StudyAK to enable full access.';
